import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, OnDestroy, Renderer2, RendererFactory2 } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { CookieService } from "ngx-cookie-service";
import { Observable, ReplaySubject, Subscription, filter, first, skip, switchMap, take } from "rxjs";
import { PlatformService } from "../platform/platform.service";
import { DataLayerService } from "../publicidad/data-layer.service";
import { TitleWrapperService } from "../title-wrapper/title-wrapper.service";
import { ConfigService } from "../config/config.service";

export type consentType = "normal" | "sub" | false;
export type consents = {
  init: boolean;
  config: boolean;
  rendimiento: boolean;
  publicidad: boolean;
};

@Injectable({
  providedIn: "root",
})
/**
 * El servicio es responsable de gestionar los consentimientos del usuario y de inicializar
 * los servicios de publicidad y rendimiento.
 */
export class ConsentService implements OnDestroy {
  private renderer2: Renderer2;

  // Subject para controlar el tipo de consentimiento.
  private _consentType: ReplaySubject<consentType> = new ReplaySubject(1);
  private _consents: ReplaySubject<consents> = new ReplaySubject(1);

  // Subject para controlar si se ha inicializado la publicidad.
  public publicidadInitSubject: ReplaySubject<boolean> = new ReplaySubject(1);

  // Variables privadas para controlar si se han inicializado los servicios de publicidad y rendimiento.
  private _preAnalytics: boolean = false;
  private _prePubli: boolean = false;

  // Suscripciones a eventos de cambio de título y navegación para enviar datos de Google Tag Manager.
  private titleSub: Subscription;
  private adsSub: Subscription;

  constructor(
    private cookieService: CookieService,
    @Inject(DOCUMENT) private document,
    private configService: ConfigService,
    private renderFactory2: RendererFactory2,
    private dataLayerService: DataLayerService,
    private titleWrapperService: TitleWrapperService,
    private router: Router,
    private platformService: PlatformService,
  ) {
    this.renderer2 = this.renderFactory2.createRenderer(null, null);

    const configPresna = this.configService.getConfigPrensa();
    if (configPresna) {
      configPresna.pipe(first()).subscribe((result) => {
        if (Boolean(result.general["global-constants"].usar_gtm) === true) {
          this.getCookie();
        }
        if (result.general["app-modal-politica-co"]?.["is_consent"] === "true") {
          this._consentType.next("normal");
        } else if (result.general["app-modal-politica-co"]?.["is_consent_sub"] === "true") {
          this._consentType.next("sub");
        } else {
          this._consentType.next(false);
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.titleSub) this.titleSub.unsubscribe();
    if (this.adsSub) this.adsSub.unsubscribe();
  }

  /* --------------GETTERS Y SETTERS-------------- */

  public get consentType(): Observable<consentType> {
    return this._consentType.asObservable();
  }

  public set consentType(value: consentType) {
    this._consentType.next(value);
  }

  public get consents(): Observable<consents> {
    return this._consents.asObservable();
  }

  /**
   * La función establece los consentimientos y, si es necesario, inicializa los servicios de publicidad y rendimiento.
   */
  public set consents(value: consents) {
    if (value.publicidad && !this._prePubli) {
      this.initPublicidad();
    }
    if (value.rendimiento && !this._preAnalytics) {
      this._preAnalytics = true;
      this.setRendimiento(true);
    }
    this._consents.next(value);
  }

  public setInit(init: boolean): void {
    this.updateConsents({ init });
  }

  public setConfig(config: boolean): void {
    this.updateConsents({ config });
  }

  /**
   * La función establece la propiedad de rendimiento e inicializa el rendimiento si es verdadero y
   * preAnalytics es falso.
   * @param {boolean} rendimiento - Un valor booleano que indica si la función de rendimiento está
   * habilitada o no.
   */
  public setRendimiento(rendimiento: boolean): void {
    if (rendimiento) {
      this._preAnalytics = true;
      this.initRendimiento();
    }
    this.updateConsents({ rendimiento });
  }

  /**
   * La función establece el valor de una propiedad publicitaria y actualiza los consentimientos.
   * @param {boolean} publicidad - El parámetro "publicidad" es un valor booleano que representa si el
   * usuario ha dado su consentimiento para mostrar anuncios.
   */
  public setPublicidad(publicidad: boolean): void {
    if (publicidad && !this._prePubli) {
      this._prePubli = true;
      this.initPublicidad();
    }
    this.updateConsents({ publicidad });
  }

  /* La propiedades son get y set de la variable privada `_preAnalytics` */
  public get preAnalytics(): boolean {
    return this._preAnalytics;
  }

  public set preAnalytics(value: boolean) {
    this._preAnalytics = value;
  }

  /* La propiedades son get y set de la variable privada `_prePubli` */
  public get prePubli(): boolean {
    return this._prePubli;
  }

  public set prePubli(value: boolean) {
    this._prePubli = value;
  }

  /* -----------------FUNCIONES----------------- */

  /**
   * La función actualiza los consentimientos fusionando los consentimientos actuales con el valor
   * proporcionado.
   * @param value - El parámetro `valor` es de tipo `Partial<consentimiento>`, lo que significa que
   * puede ser un objeto que contenga algunas o todas las propiedades del tipo `consentimiento`.
   */
  private updateConsents(value: Partial<consents>) {
    if (this._consents) {
      this._consents.pipe(first()).subscribe((currentConsents) => {
        this._consents.next({ ...currentConsents, ...value });
      });
    }
  }

  /**
   * La función guarda los consentimientos en una cookie llamada 'Consent' con una fecha de expiración de un año.
   */
  public saveCookie() {
    const expirationDate = new Date();
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    this.updateConsents({ init: true, config: false });
    if (this.platformService.isBrowser()) {
      this.renderer2.setStyle(this.document.body, "overflow", "auto");
    }
    if (this._consents) {
      this._consents.pipe(first()).subscribe((currentConsents) => {
        if (this.platformService.isBrowser()) {
          this.cookieService.set("Consent", JSON.stringify(currentConsents), { expires: expirationDate, path: "/" });
        }
      });
    }
  }

  /**
   * La función recupera una cookie llamada 'Consent' y asigna su valor a la variable
   * 'consents', o inicializa 'consents' con valores predeterminados si la cookie no
   * existe.
   */
  private getCookie() {
    if (this.platformService.isBrowser()) {
      const cookie = this.cookieService.get("Consent");
      if (!cookie) {
        this.consents = {
          init: false,
          config: false,
          rendimiento: false,
          publicidad: false,
        };
        return;
      }
      this.consents = JSON.parse(cookie);
    }
  }

  /* Suscripción para detectar el cambio de navegacion + cambio de titulo.
  Con esto aseguro que se haya cambiado el título después de navegar para mandar
  los datos de Google Tag Manager a través del dataLayer. */
  initRendimiento() {
    if (!this._preAnalytics) {
      this.dataLayerService.sendPageView();
    }
  }

  /**
   * La función inicializa la publicidad e inicializa el dataLayer y las suscripciones a eventos de cambio de título y navegación.
   */
  initPublicidad() {
    if (this._prePubli) return;
    this._prePubli = true;
    this.publicidadInitSubject.next(true);
    const title = this.titleWrapperService.getTitle();
    if (title) {
      title.pipe(take(1)).subscribe(() => {
        // this.dataLayerService.logPageView();
      });
    }

    if (this.router?.events) {
      this.titleSub = this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          filter((event, index) => index > 0),
          switchMap(() => {
            const title = this.titleWrapperService.getTitle();
            if (title) {
              return title.pipe(take(1));
            }
          }),
        )
        .subscribe(() => {
          // console.log("Pruebas Analítica - Cambio de titulo");
          // console.log('Pruebas Analítica - Forzando page_view desde CONSENT SERVICE...');
          // this.gaService.pageView('/prueba-pageview');
        });
    }

    if (this.router?.events) {
      this.adsSub = this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          skip(1),
          switchMap(() => {
            const title = this.titleWrapperService.getTitle();
            if (title) {
              return title.pipe(take(1));
            }
          }),
        )
        .subscribe(() => {
          this.dataLayerService.logAdsRefresh();
        });
    }
  }
}
